import { Building2, Pencil } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip'
import { ORG_APPROVAL_STATUS_LABELS, labelOf } from '@/shared/constants'
import type { MyOrganization } from '../hooks/useMyOrganization'

// La etiqueta sale del módulo central de traducciones; aquí solo el color.
const APPROVAL_VARIANT: Record<string, 'active' | 'inactive' | 'cancelled'> = {
  ACTIVE: 'active',
  PARTIAL_ACTIVE: 'inactive',
  NO_ACTIVE: 'cancelled',
  // Alias legacy.
  APPROVED: 'active',
  PENDING: 'inactive',
  REJECTED: 'cancelled',
}

interface OrganizationHeaderProps {
  organization: NonNullable<MyOrganization>
  /** Si se provee, muestra un ícono Pencil junto al nombre (modo vista). */
  onEdit?: () => void
}

export function OrganizationHeader({ organization, onEdit }: OrganizationHeaderProps) {
  const approval = organization.approvalStatus
    ? {
        label: labelOf(ORG_APPROVAL_STATUS_LABELS, organization.approvalStatus),
        variant: APPROVAL_VARIANT[organization.approvalStatus.toUpperCase()] ?? 'inactive',
      }
    : null

  return (
    <header className="flex flex-wrap items-start justify-between gap-6">
      <div className="flex items-center gap-4">
        <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-card border border-line bg-surface">
          {organization.logo ? (
            <img
              src={organization.logo}
              alt={organization.legalName ?? 'Logo'}
              className="h-full w-full object-cover"
            />
          ) : (
            <Building2 className="h-8 w-8 text-ink-subtle" />
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="font-serif text-[28px] font-normal leading-[1.1] tracking-tight text-ink sm:text-[36px]">
              {organization.legalName ?? 'Mi organización'}
            </h1>
            {onEdit && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    aria-label="Editar"
                    onClick={onEdit}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Editar</TooltipContent>
              </Tooltip>
            )}
          </div>
          {organization.propietaryName && (
            <p className="mt-1 text-sm text-ink-muted">
              Propietario: <span className="text-ink">{organization.propietaryName}</span>
            </p>
          )}
        </div>
      </div>
      {approval && <Badge variant={approval.variant}>{approval.label}</Badge>}
    </header>
  )
}
